import * as Sentry from "@sentry/react";

const PII_KEYS = ["email", "phone", "password", "token", "authorization", "auth"];

const scrubPii = (input: unknown): unknown => {
  if (input == null) return input;
  if (Array.isArray(input)) return input.map(scrubPii);
  if (typeof input !== "object") return input;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
    if (PII_KEYS.includes(k.toLowerCase())) {
      out[k] = "[REDACTED]";
    } else {
      out[k] = scrubPii(v);
    }
  }
  return out;
};

export interface ReportErrorOptions {
  operation: string;
  area?: string;
  extra?: Record<string, unknown>;
  level?: Sentry.SeverityLevel;
}

export const reportError = (error: unknown, options: ReportErrorOptions): string | undefined => {
  const tags: Record<string, string> = { operation: options.operation };
  if (options.area) tags.area = options.area;

  return Sentry.captureException(error, {
    tags,
    extra: scrubPii(options.extra ?? {}) as Record<string, unknown>,
    level: options.level,
  });
};

export interface ReportContextOptions {
  operation: string;
  area?: string;
  contextName: string;
  contextData: Record<string, unknown>;
  extra?: Record<string, unknown>;
}

export const reportErrorWithContext = (
  error: unknown,
  options: ReportContextOptions,
): string | undefined => {
  const tags: Record<string, string> = { operation: options.operation };
  if (options.area) tags.area = options.area;

  return Sentry.withScope((scope) => {
    Object.entries(tags).forEach(([k, v]) => scope.setTag(k, v));
    scope.setContext(options.contextName, scrubPii(options.contextData) as Record<string, unknown>);
    if (options.extra) {
      scope.setExtra("data", scrubPii(options.extra) as Record<string, unknown>);
    }
    return Sentry.captureException(error);
  });
};

export const setPageArea = (area: string): void => {
  Sentry.setTag("area", area);
};

export interface GymTags {
  gym_id?: string | null;
  name?: string | null;
  slug?: string | null;
}

export const setGymTags = (gym: GymTags): void => {
  if (gym.gym_id) Sentry.setTag("gym_id", gym.gym_id);
  if (gym.name) Sentry.setTag("gym_name", gym.name);
  if (gym.slug) Sentry.setTag("gym_slug", gym.slug);
};

export const clearGymTags = (): void => {
  Sentry.setTag("gym_id", "");
  Sentry.setTag("gym_name", "");
  Sentry.setTag("gym_slug", "");
};

export const clearSentryUser = (): void => {
  Sentry.setUser(null);
  Sentry.setTag("role", "");
  Sentry.setTag("gym_id", "");
  Sentry.setTag("gym_name", "");
  Sentry.setTag("gym_slug", "");
};
